import { useState, useEffect } from 'react';
import { rmmAPI } from '@/lib/api';
import { Loader2 } from 'lucide-react';

const COLORS = { online: '#10b981', offline: '#ef4444', unknown: '#94a3b8' };
const KIND_ICON = {
  site: '🏢', router: 'RTR', firewall: 'FW', switch: 'SW', wireless_ap: 'AP',
  printer: 'PRN', ups: 'UPS', nas: 'NAS', server: 'SRV', vm: 'VM', camera: 'CAM',
  workstation: 'WS', laptop: 'LT', other: 'DEV',
};

export const TopologyView = ({ siteId }) => {
  const [topo, setTopo] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!siteId) return;
    setLoading(true);
    rmmAPI.topology(siteId).then((r) => setTopo(r.data)).catch(() => setTopo(null)).finally(() => setLoading(false));
  }, [siteId]);

  if (!siteId) return <p className="text-sm text-slate-500 py-6 text-center">Select a site to render its live network map.</p>;
  if (loading) return <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-cyan-500" /></div>;
  if (!topo || topo.nodes.length <= 1) return <p className="text-sm text-slate-500 py-6 text-center">No monitored devices at this site yet.</p>;

  // BFS depth from "site"
  const childMap = {};
  topo.edges.forEach((e) => { (childMap[e.from] = childMap[e.from] || []).push(e.to); });
  const depth = { site: 0 };
  const queue = ['site'];
  while (queue.length) {
    const n = queue.shift();
    (childMap[n] || []).forEach((c) => { if (depth[c] === undefined) { depth[c] = depth[n] + 1; queue.push(c); } });
  }
  topo.nodes.forEach((n) => { if (depth[n.id] === undefined) depth[n.id] = 1; });

  const cols = {};
  topo.nodes.forEach((n) => { (cols[depth[n.id]] = cols[depth[n.id]] || []).push(n); });
  const NW = 160, NH = 40, XGAP = 220, YGAP = 56;
  const pos = {};
  const maxRows = Math.max(...Object.values(cols).map((c) => c.length));
  const height = Math.max(240, maxRows * YGAP + 60);
  Object.entries(cols).forEach(([d, nodes]) => {
    const startY = (height - nodes.length * YGAP) / 2;
    nodes.forEach((n, i) => { pos[n.id] = { x: Number(d) * XGAP + 20, y: startY + i * YGAP + 20 }; });
  });
  const width = (Math.max(...Object.keys(cols).map(Number)) + 1) * XGAP + 40;

  return (
    <div className="overflow-x-auto" data-testid="rmm-topology-map">
      <svg width={width} height={height} className="min-w-full">
        {topo.edges.map((e, i) => {
          const a = pos[e.from]; const b = pos[e.to];
          if (!a || !b) return null;
          const downstream = topo.nodes.find((n) => n.id === e.to);
          return <path key={i} d={`M ${a.x + NW} ${a.y + NH / 2} C ${a.x + NW + 40} ${a.y + NH / 2}, ${b.x - 40} ${b.y + NH / 2}, ${b.x} ${b.y + NH / 2}`}
            stroke={downstream?.status === 'offline' ? '#fca5a5' : '#cbd5e1'} strokeWidth="1.5" fill="none"
            strokeDasharray={downstream?.status === 'offline' ? '4 3' : 'none'} />;
        })}
        {topo.nodes.map((n) => {
          const p = pos[n.id];
          if (!p) return null;
          const color = n.kind === 'site' ? '#06b6d4' : COLORS[n.status] || COLORS.unknown;
          return (
            <g key={n.id} data-testid={`topo-node-${n.kind}`}>
              <rect x={p.x} y={p.y} width={NW} height={NH} rx="8" fill="white" stroke={color} strokeWidth="1.5" />
              <circle cx={p.x + 12} cy={p.y + NH / 2} r="4" fill={color} />
              <text x={p.x + 24} y={p.y + 17} fontSize="11" fontWeight="600" fill="#1e293b">
                {(n.label || '').slice(0, 18)}
              </text>
              <text x={p.x + 24} y={p.y + 31} fontSize="9" fill="#64748b">
                {KIND_ICON[n.kind] || n.kind}{n.ip ? ` · ${n.ip}` : ''}{n.is_gateway ? ' · GW' : ''}
              </text>
            </g>
          );
        })}
      </svg>
      <div className="flex gap-4 text-xs text-slate-500 mt-1 pl-1">
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500 inline-block" />Online</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500 inline-block" />Offline</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-slate-400 inline-block" />Unknown</span>
      </div>
    </div>
  );
};
